import React, { useEffect, useState, useRef } from "react";
import { Modal, Select, Spin, Button } from "antd";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine";
import domtoimage from "dom-to-image";
import { jsPDF } from "jspdf";
import { httpService } from "../../../services/httpService.service";
import "./MapComponent.scss";

const { Option } = Select;

// Custom icon factory
const createCustomIcon = (color = "#f44336", iconHtml = "") => {
  const svgIcon = `
    <div style="position: relative; width: 40px; height: 60px;">
      <div style="position: absolute; top: 0; left: 0; z-index: 1;">
        <svg width="40" height="60" viewBox="0 0 40 60" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 0C9 0 0 9 0 20c0 11 20 40 20 40s20-29 20-40C40 9 31 0 20 0z" fill="${color}" />
        </svg>
      </div>
      <div style="position: absolute; top: 5px; left: 5px; z-index: 2; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;">
        ${iconHtml}
      </div>
    </div>
  `;

  return L.divIcon({
    className: "custom-pin-icon",
    html: svgIcon,
    iconSize: [40, 60],
    iconAnchor: [20, 60],
    popupAnchor: [0, -60],
  });
};

const RoutingMachine = ({ from, to, onDistanceChange }) => {
  const map = useMap();
  const routingControlRef = useRef(null);

  useEffect(() => {
    if (!from || !to) return;

    if (routingControlRef.current) {
      map.removeControl(routingControlRef.current);
      routingControlRef.current = null;
    }

    const routingControl = L.Routing.control({
      waypoints: [L.latLng(from.lat, from.lng), L.latLng(to.lat, to.lng)],
      lineOptions: {
        styles: [{ color: "red", weight: 4 }],
      },
      addWaypoints: false,
      routeWhileDragging: false,
      draggableWaypoints: false,
      fitSelectedRoutes: false,
      showAlternatives: false,
      createMarker: () => null,
    })
      .on("routesfound", (e) => {
        const route = e.routes[0];
        const distanceInKm = (route.summary.totalDistance / 1000).toFixed(2);
        if (onDistanceChange) {
          onDistanceChange(distanceInKm);
        }
      })
      .addTo(map);

    routingControlRef.current = routingControl;

    return () => {
      if (routingControlRef.current) {
        map.removeControl(routingControlRef.current);
        routingControlRef.current = null;
      }
    };
  }, [from, to, map, onDistanceChange]);

  return null;
};

const DistanceModal = ({ visible, onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite1, setSelectedSite1] = useState(null);
  const [selectedSite2, setSelectedSite2] = useState(null);
  const [distance, setDistance] = useState(null);
  const mapRef = useRef(null);
  const modalContentRef = useRef(null);
  const projectId = localStorage.getItem("currentProjectID");

  useEffect(() => {
    if (visible) {
      setLoading(true);
      setDistance(null);
      httpService.get(
        `/api/Site/getAllSiteVisits?projectId=${projectId}&pageNumber=1&pageSize=1000`,
        (res) => {
          setData(res.data);
          setLoading(false);
        },
        (error) => {
          console.error(error);
          setLoading(false);
        }
      );
    }
  }, [visible, projectId]);

  useEffect(() => {
    if (visible && mapRef.current) {
      setTimeout(() => {
        mapRef.current.invalidateSize();
      }, 200);
    }
  }, [visible]);

  const handleSelect1 = (value) => {
    const site = data.find((item) => item.id === value);
    setSelectedSite1(site);
    setDistance(null);
  };

  const handleSelect2 = (value) => {
    const site = data.find((item) => item.id === value);
    setSelectedSite2(site);
    setDistance(null);
  };

  const handleClose = () => {
    setSelectedSite1(null);
    setSelectedSite2(null);
    setDistance(null);
    onClose();
  };

const handleDownloadPdf = () => {
  if (!modalContentRef.current) return;

  domtoimage
    .toPng(modalContentRef.current)
    .then((dataUrl) => {
      const pdf = new jsPDF("portrait", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const img = new Image();
      img.src = dataUrl;

      img.onload = () => {
        const imgWidth = img.width;
        const imgHeight = img.height;

        // Calculate scale to fit image inside PDF page with some margin
        const margin = 20;
        const maxWidth = pdfWidth - 2 * margin;
        const maxHeight = pdfHeight - 2 * margin;

        let renderWidth = imgWidth;
        let renderHeight = imgHeight;

        // Scale image proportionally to fit in PDF page
        if (imgWidth > maxWidth || imgHeight > maxHeight) {
          const widthRatio = maxWidth / imgWidth;
          const heightRatio = maxHeight / imgHeight;
          const scale = Math.min(widthRatio, heightRatio);
          renderWidth = imgWidth * scale;
          renderHeight = imgHeight * scale;
        }

        // Add image to PDF centered with margin
        const x = (pdfWidth - renderWidth) / 2;
        const y = (pdfHeight - renderHeight) / 2;

        pdf.addImage(dataUrl, "PNG", x, y, renderWidth, renderHeight);
        pdf.save("distance-modal.pdf");
      };
    })
    .catch((error) => {
      console.error("Error generating PDF:", error);
    });
};


  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={
        <>
          <Button onClick={handleClose}>Close</Button>
          <Button onClick={handleDownloadPdf} type="primary" style={{ marginLeft: 10 }}>
            Download PDF
          </Button>
        </>
      }
      width={1000}
      title="Distance Between Two Site Visits"
    >
      <div ref={modalContentRef} style={{ padding: "10px" }}>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Spin size="small" />
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
              <div style={{ flex: 1 }}>
                <label>Site Visit 1</label>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select Site Visit 1"
                  onChange={handleSelect1}
                  value={selectedSite1?.id}
                  allowClear
                >
                  {data.map((site) => (
                    <Option key={site.id} value={site.id}>
                      {site.name ? `${site.name}` : site.identifier}
                    </Option>
                  ))}
                </Select>
              </div>
              <div style={{ flex: 1 }}>
                <label>Site Visit 2</label>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select Site Visit 2"
                  onChange={handleSelect2}
                  value={selectedSite2?.id}
                  allowClear
                >
                  {data.map((site) => (
                    <Option key={site.id} value={site.id}>
                      {site.name ? `${site.name}` : site.identifier}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>

            {distance && (
              <div
                style={{
                  marginBottom: "10px",
                  fontWeight: "bold",
                  fontSize: "16px",
                  textAlign: "center",
                }}
              >
                Distance: {distance} km
              </div>
            )}

            <div style={{ marginBottom: "20px", width: "100%", height: "400px" }}>
              <MapContainer
                center={[20, 0]}
                zoom={2}
                minZoom={1}
                maxBounds={[
                  [-90, -180],
                  [90, 180],
                ]}
                style={{ height: "100%", width: "100%" }}
                whenCreated={(mapInstance) => {
                  mapRef.current = mapInstance;
                }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                {selectedSite1?.locationDetails?.latitude && (
                  <Marker
                    position={[
                      selectedSite1.locationDetails.latitude,
                      selectedSite1.locationDetails.longitude,
                    ]}
                    icon={createCustomIcon("#1976d2")}
                  >
                    <Popup>{selectedSite1.name}</Popup>
                  </Marker>
                )}

                {selectedSite2?.locationDetails?.latitude && (
                  <Marker
                    position={[
                      selectedSite2.locationDetails.latitude,
                      selectedSite2.locationDetails.longitude,
                    ]}
                    icon={createCustomIcon("#d32f2f")}
                  >
                    <Popup>{selectedSite2.name}</Popup>
                  </Marker>
                )}

                {selectedSite1?.locationDetails?.latitude &&
                  selectedSite2?.locationDetails?.latitude && (
                    <RoutingMachine
                      from={{
                        lat: selectedSite1.locationDetails.latitude,
                        lng: selectedSite1.locationDetails.longitude,
                      }}
                      to={{
                        lat: selectedSite2.locationDetails.latitude,
                        lng: selectedSite2.locationDetails.longitude,
                      }}
                      onDistanceChange={setDistance}
                    />
                  )}
              </MapContainer>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default DistanceModal;
